import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { CheckCircleIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CRMIntegrationService } from '@obis/integration';

interface FeeStructureSectionProps {
  selectedClass: string;
  selectedGender?: string;
  onDiscountApplied: (discountData: any) => void;
  token: string;
}

interface FeeStructure {
  tuitionFee: number;
  admissionFeeMin: number;
  admissionFeeMax: number;
  uniformFee: number;
  booksAndCopies: number;
  examFee: number;
  appFee: number;
  transportFee: number;
  libraryFee: number;
  activityFee: number;
  maxDiscountAllowed: number;
}

interface DiscountData {
  discountAmount: number;
  discountReason: string;
  discountNotes: string;
}

interface TierInfo {
  tier: number | null;
  status: 'auto_approved' | 'pending_approval' | 'rejected';
  description: string;
}

const FeeStructureSection: React.FC<FeeStructureSectionProps> = ({
  selectedClass,
  selectedGender,
  onDiscountApplied,
  token
}) => {
  const [feeStructure, setFeeStructure] = useState<FeeStructure | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [discountData, setDiscountData] = useState<DiscountData>({
    discountAmount: 0,
    discountReason: '',
    discountNotes: ''
  });
  
  const [tierInfo, setTierInfo] = useState<TierInfo | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [finalMonthlyFee, setFinalMonthlyFee] = useState(0);

  const discountReasons = [
    'Sales Incentive',
    'Scholarship',
    'Hardship',
    'Sibling Discount',
    'Staff Child',
    'Other'
  ];

  // Load fee structure when class or gender changes
  useEffect(() => {
    if (selectedClass && selectedGender) {
      loadFeeStructure();
    }
  }, [selectedClass, selectedGender]);

  // Recalculate tier and validation when discount changes
  useEffect(() => {
    if (feeStructure && discountData.discountAmount > 0) {
      calculateTierAndValidate();
    } else {
      setTierInfo(null);
      setValidationErrors([]);
      setFinalMonthlyFee(feeStructure?.tuitionFee || 0);
    }
  }, [discountData.discountAmount, feeStructure]);

  const loadFeeStructure = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use CRMIntegrationService to fetch from Firestore
      const structure = await CRMIntegrationService.getFeeStructureFromERP(selectedClass, selectedGender || '');
      
      if (structure) {
        setFeeStructure(structure);
        setFinalMonthlyFee(structure.tuitionFee);
      } else {
        setError(`Fee structure not found for ${selectedClass} (${selectedGender})`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load fee structure');
    } finally {
      setLoading(false);
    }
  };

  const calculateTierAndValidate = async () => {
    try {
      // Calculate tier
      const tierResponse = await fetch(`${API_BASE_URL}/fee-structures/tier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ discountAmount: discountData.discountAmount })
      });
      
      const tierData = await tierResponse.json();
      setTierInfo(tierData.data);
      
      // Validate discount
      const validateResponse = await fetch(`${API_BASE_URL}/fee-structures/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          discountAmount: discountData.discountAmount,
          classLevel: selectedClass
        })
      });
      
      const validateData = await validateResponse.json();
      
      if (validateData.success) {
        setValidationErrors([]);
        setFinalMonthlyFee(validateData.data.finalMonthlyFee);
      } else {
        setValidationErrors(validateData.errors || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to calculate tier');
    }
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDiscountData({
      ...discountData,
      [name]: name === 'discountAmount' ? parseFloat(value) || 0 : value
    });
  };

  // Notify parent when discount is applied
  const handleApplyDiscount = () => {
    if (tierInfo && validationErrors.length === 0) {
      onDiscountApplied({
        ...discountData,
        tier: tierInfo.tier,
        status: tierInfo.status,
        finalMonthlyFee
      });
    }
  };

  // Auto-call onDiscountApplied when discount is valid
  React.useEffect(() => {
    if (tierInfo && validationErrors.length === 0 && discountData.discountAmount > 0) {
      handleApplyDiscount();
    }
  }, [tierInfo, validationErrors, discountData.discountAmount, finalMonthlyFee, onDiscountApplied]);

  if (!selectedClass || !selectedGender) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
        <p className="text-sm">Please select a class and gender to view fee structure</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-gray-600">Loading fee structure...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!feeStructure) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Fee Structure Display */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Structure (Read-Only)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="block text-xs font-medium text-gray-600 uppercase">Monthly Tuition Fee</label>
            <p className="text-lg font-semibold text-gray-900 mt-1">₹{feeStructure.tuitionFee}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 uppercase">Admission Fee Range</label>
            <p className="text-lg font-semibold text-gray-900 mt-1">₹{feeStructure.admissionFeeMin} - ₹{feeStructure.admissionFeeMax}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 uppercase">Uniform Fee</label>
            <p className="text-lg font-semibold text-gray-900 mt-1">₹{feeStructure.uniformFee}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 uppercase">Books & Copies</label>
            <p className="text-lg font-semibold text-gray-900 mt-1">₹{feeStructure.booksAndCopies}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 uppercase">Max Discount Allowed</label>
            <p className="text-lg font-semibold text-gray-900 mt-1">₹{feeStructure.maxDiscountAllowed}</p>
          </div>
        </div>
      </div>

      {/* Sales Discount Input Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales Discount</h3>
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Amount (₹)
              </label>
              <input
                type="number"
                name="discountAmount"
                value={discountData.discountAmount}
                onChange={handleDiscountChange}
                max={feeStructure.maxDiscountAllowed}
                min="0"
                className="input-field"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Max: ₹{feeStructure.maxDiscountAllowed}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Reason *
              </label>
              <select
                name="discountReason"
                value={discountData.discountReason}
                onChange={handleDiscountChange}
                className="input-field"
              >
                <option value="">Select reason</option>
                {discountReasons.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              name="discountNotes"
              value={discountData.discountNotes}
              onChange={handleDiscountChange}
              className="input-field"
              rows={2}
              placeholder="Add any notes about this discount..."
            />
          </div>
        </div>
      </div>

      {/* Tier Information Box */}
      {tierInfo && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <h4 className="font-semibold text-gray-900 mb-3">Discount Tier Information</h4>
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              <strong>Tier 1:</strong> ₹0-100 → Auto-Approved ✓
            </p>
            <p>
              <strong>Tier 2:</strong> ₹101-200 → Auto-Approved ✓
            </p>
            <p>
              <strong>Tier 3:</strong> ₹201-300 → Requires Admin Approval ⏳
            </p>
          </div>
        </div>
      )}

      {/* Approval Status */}
      {tierInfo && discountData.discountAmount > 0 && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          tierInfo.status === 'auto_approved' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          {tierInfo.status === 'auto_approved' ? (
            <>
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-900">{tierInfo.description}</p>
                <p className="text-sm text-green-700">This discount will be auto-approved</p>
              </div>
            </>
          ) : (
            <>
              <ClockIcon className="h-6 w-6 text-yellow-600" />
              <div>
                <p className="font-semibold text-yellow-900">{tierInfo.description}</p>
                <p className="text-sm text-yellow-700">This discount requires admin approval</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900 mb-2">Validation Errors:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Fee Breakdown */}
      {discountData.discountAmount > 0 && validationErrors.length === 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Fee Breakdown</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-700">Base Monthly Fee:</span>
              <span className="font-semibold">₹{feeStructure.tuitionFee}</span>
            </div>
            <div className="flex justify-between text-red-600">
              <span>Sales Discount:</span>
              <span className="font-semibold">- ₹{discountData.discountAmount}</span>
            </div>
            <div className="border-t border-blue-300 pt-2 flex justify-between">
              <span className="font-semibold text-gray-900">Final Monthly Fee:</span>
              <span className="font-bold text-lg text-blue-600">₹{finalMonthlyFee}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeStructureSection;
